import { confidenceLabel } from '../ocr/confidence-policy.js';
import { regionStyle } from './coordinate-mapper.js';
import { fittedFontSize, isRtl } from './text-fit.js';

function node(tag, className, text = '') {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
}

export class OverlayRenderer {
  constructor({ stage, list, details, onSpeak, onSave }) {
    this.stage = stage;
    this.list = list;
    this.details = details;
    this.onSpeak = onSpeak;
    this.onSave = onSave;
    this.pageIndex = 0;
    this.opacity = 0.92;
    this.zoom = 1;
    this.rotation = 0;
  }

  setDocument(document) {
    this.document = document;
    this.pageIndex = 0;
    this.render();
  }

  setPage(index) {
    this.pageIndex = Math.max(0, Math.min(index, this.document.pages.length - 1));
    this.render();
  }

  setOpacity(value) {
    this.opacity = Number(value);
    this.stage.style.setProperty('--ocr-overlay-opacity', String(this.opacity));
  }

  setTransform({ zoom = this.zoom, rotation = this.rotation }) {
    this.zoom = zoom;
    this.rotation = rotation;
    const canvas = this.stage.querySelector('.ocr-canvas');
    if (canvas) canvas.style.transform = `scale(${zoom}) rotate(${rotation}deg)`;
  }

  render() {
    const page = this.document?.pages?.[this.pageIndex];
    if (!page) return;
    this.stage.replaceChildren();
    this.list.replaceChildren();
    this.details.replaceChildren();

    const canvas = node('div', 'ocr-canvas');
    canvas.style.aspectRatio = `${page.width} / ${page.height}`;
    canvas.style.transform = `scale(${this.zoom}) rotate(${this.rotation}deg)`;
    const image = node('img', 'ocr-page-image');
    image.src = page.imageUrl;
    image.alt = `Page ${page.pageNumber} de ${this.document.source.name}`;
    canvas.append(image);

    const overlay = node('div', 'ocr-overlay-layer');
    for (const region of page.regions) {
      const translated = region.translation.compactText || region.translation.text || region.text;
      const button = node('button', 'ocr-region', translated);
      button.type = 'button';
      button.dataset.regionId = region.id;
      Object.assign(button.style, regionStyle(region));
      button.style.fontSize = `${fittedFontSize(region, translated)}px`;
      button.dir = isRtl(region, translated) ? 'rtl' : 'ltr';
      button.setAttribute('aria-label', `${region.text}. ${translated}`);
      button.addEventListener('click', () => this.select(region, button));
      overlay.append(button);

      const row = node('button', 'ocr-region-row');
      row.type = 'button';
      row.append(node('span', 'ocr-region-source', region.text));
      row.append(node('span', 'ocr-region-target', translated));
      row.addEventListener('click', () => this.select(region, button));
      this.list.append(row);
    }
    canvas.append(overlay);
    this.stage.append(canvas);
    this.stage.style.setProperty('--ocr-overlay-opacity', String(this.opacity));
  }

  select(region, overlayButton) {
    this.stage.querySelectorAll('.ocr-region').forEach(item => item.classList.toggle('selected', item === overlayButton));
    this.details.replaceChildren();
    const confidence = confidenceLabel(region.confidence);
    this.details.append(
      node('div', 'ocr-detail-original', region.text),
      node('div', 'ocr-detail-translation', region.translation.text || 'Traduction indisponible'),
      node('span', `ocr-confidence ${confidence.tone}`, `${confidence.label} · ${Math.round(region.confidence * 100)} %`)
    );
    for (const warning of region.translation.warnings) this.details.append(node('p', 'ocr-detail-warning', String(warning)));
    const actions = node('div', 'ocr-detail-actions');
    const speak = node('button', 'secondary-button', 'Écouter');
    speak.type = 'button';
    speak.addEventListener('click', () => this.onSpeak?.(region.translation.text || region.text));
    const save = node('button', 'secondary-button', 'Ajouter aux mots appris');
    save.type = 'button';
    save.addEventListener('click', () => this.onSave?.(region));
    actions.append(speak, save);
    this.details.append(actions);
  }
}
